import PrintIcon from "@dana/Icons/PrintIcon";
import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import Icon from "@insite/mobius/Icon";
import React, { forwardRef } from "react";
import styled from "styled-components";

type PrintButtonProps = {
    /** Optional: print only a specific area */
    targetRef?: React.RefObject<HTMLElement | null>;
    /** ARIA label and tooltip text (defaults to "Print") */
    label?: string;
    /** Icon size in px */
    size?: number;
    /** Disable the button */
    disabled?: boolean;
    /** Class name passthrough for styled-system or overrides */
    className?: string;
    /** Optional: run logic before print (supports async) */
    onBeforePrint?: () => void | Promise<void>;
    /** Optional: run logic after print */
    onAfterPrint?: () => void;
};

const StyledPrintButton = styled.button`
    padding: 4px;
    background-color: transparent;
    border: none;
    cursor: pointer;

    &:hover {
        color: ${themeColors.secondary.danaDarkBlue};
    }

    &:focus-visible {
        color: ${themeColors.secondary.danaDarkBlue};
        border: 1px solid ${themeColors.primary.white};
        box-shadow: ${elevation.focus};
    }

    @media print {
        display: none; /* Hide during print */
    }
`;

function collectStyledComponentsCSS(): string {
    const styleEls = Array.from(document.querySelectorAll<HTMLStyleElement>("style[data-styled]"));
    let css = "";
    for (const el of styleEls) {
        const sheet = el.sheet as CSSStyleSheet | null;
        if (!sheet) {
            continue;
        }
        try {
            const rules = Array.from(sheet.cssRules);
            for (const rule of rules) {
                css += `${rule.cssText}\n`;
            }
        } catch (err) {
            // Shouldn’t happen for same-origin inline <style>, but guard anyway
            console.warn("Unable to read styled-components cssRules:", err);
        }
    }
    return css;
}

export const PrintButton = forwardRef<HTMLButtonElement, PrintButtonProps>(
    ({ targetRef, label = "Print", size = 28, disabled, className, onBeforePrint, onAfterPrint }, ref) => {
        const handlePrint = async () => {
            if (disabled) {
                return;
            }

            // SSR safety: only run in the browser
            if (typeof window === "undefined" || typeof document === "undefined") {
                return;
            }

            try {
                await onBeforePrint?.();
            } catch {
                // Swallow to avoid blocking print in failure of before hook
            }

            const target = targetRef?.current;

            if (target) {
                // Print only the referenced element via a hidden iframe
                const iframe = document.createElement("iframe");
                iframe.style.position = "fixed";
                iframe.style.right = "0";
                iframe.style.bottom = "0";
                iframe.style.width = "0";
                iframe.style.height = "0";
                iframe.style.border = "0";
                document.body.appendChild(iframe);

                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!doc) {
                    document.body.removeChild(iframe);
                    window.print();
                    onAfterPrint?.();
                    return;
                }

                // Clone stylesheets into the print document

                const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
                    .map(n => n.outerHTML)
                    .join("\n");

                const scCSS = collectStyledComponentsCSS();

                doc.open();
                doc.write(`
                <!doctype html>
                <html>
                    <head>
                    <meta charset="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <title>${document.title}</title>
                    ${links}
                    <style data-copied-from-styled>${scCSS}</style>
                    <style>
                        /* Ensure our button is hidden even if copied accidentally */
                        @media print { button { display: none !important; } }
                    </style>
                    </head>
                    <body>
                    ${target.outerHTML}
                    </body>
                </html>
                `);
                doc.close();

                const doPrint = () => {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                    // Clean up after printing
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                        onAfterPrint?.();
                    }, 0);
                };

                // If doc is ready immediately, print; otherwise wait for load
                if (iframe.contentWindow?.document?.readyState === "complete") {
                    doPrint();
                } else {
                    iframe.onload = doPrint;
                }
            } else {
                // Print the whole page
                window.print();
                onAfterPrint?.();
            }
        };

        return (
            <StyledPrintButton
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                onClick={handlePrint}
                className={className}
                disabled={disabled}
            >
                <Icon src={PrintIcon} size={size} />
            </StyledPrintButton>
        );
    },
);

PrintButton.displayName = "PrintButton";
